import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Stack, Typography } from '@mui/material';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';

const PAGE_SIZE = 20;

/** Determines the current user's result for one history item — 'win'/'loss' by comparing their own seat's team against winningTeam, 'tie' when there wasn't one. */
function myOutcome(item, userId) {
    const me = item.players.find((p) => p.userId === userId);
    if (!me || item.winningTeam === null) return 'tie';
    return me.team === item.winningTeam ? 'win' : 'loss';
}

/**
 * Finished-game history (RONDA_ONLINE.md Fase 9c) — consumes GET
 * /rooms/history, already built in Fase 8 but never wired up on the
 * client until now. Simple offset/limit pagination via a "load more"
 * button, matching what the backend itself documents (no cursor paging).
 */
export default function OnlineHistoryScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authFetch, user } = useAuth();

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const loadPage = useCallback(
        (offset) => authFetch(`/rooms/history?limit=${PAGE_SIZE}&offset=${offset}`),
        [authFetch]
    );

    const reportError = useCallback(
        (err) => setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined))),
        [t]
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await loadPage(0);
                if (!cancelled) {
                    setItems(result.items);
                    setTotal(result.total);
                }
            } catch (err) {
                if (!cancelled) reportError(err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadPage, reportError]);

    const handleLoadMore = async () => {
        setIsLoadingMore(true);
        setError(null);
        try {
            const result = await loadPage(items.length);
            setItems((prev) => [...prev, ...result.items]);
            setTotal(result.total);
        } catch (err) {
            reportError(err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const outcomeColor = (outcome) =>
        outcome === 'win' ? 'success.main' : outcome === 'loss' ? 'error.main' : 'text.primary';

    return (
        <>
            <Header title={t('online.history.title')} onBack={() => navigate('/online')} showSettings={false} />
            <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
                <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : items.length === 0 ? (
                        <Typography color="text.secondary">{t('online.history.empty')}</Typography>
                    ) : (
                        <>
                            {items.map((item) => {
                                const outcome = myOutcome(item, user?.id);
                                return (
                                    <Card key={item.id} elevation={2}>
                                        <CardContent>
                                            <Typography variant="body2" color="text.secondary">
                                                {new Date(item.finishedAt).toLocaleString()}
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: outcomeColor(outcome) }}>
                                                {t(`online.history.outcome_${outcome}`)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {item.finalScoreTeam1} - {item.finalScoreTeam2}
                                                {' · '}
                                                {t('online.history.duration', { seconds: item.durationSeconds })}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                            {items.length < total && (
                                <Button onClick={handleLoadMore} disabled={isLoadingMore}>
                                    {isLoadingMore ? <CircularProgress size={20} /> : t('online.history.load_more')}
                                </Button>
                            )}
                        </>
                    )}
                </Stack>
            </Container>
        </>
    );
}
